import React from "react";
import { CalendarDays } from "lucide-react";
import { updateFeature } from "../../../../Products/Services/ProductService";
import {
  buildNotification,
  GenerateNotification,
  isFromValid,
} from "smart-react";
import { getSubscription } from "../../../../../Utils/Auth/ValidatePermissions";
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../../../constants/eventDataTypes";
import {
  ERROR_MESSAGE,
  UPDATE_MESSAGE,
} from "../../../../../constants/notificationMessages";
import {
  ProductButton,
  ProductSheet,
  SpinnerIcon,
} from "../ProductListPrimitives";

const toDateInputValue = (value) => {
  if (!value) {
    return "";
  }

  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) {
    return "";
  }

  const year = dateValue.getFullYear();
  const month = `${dateValue.getMonth() + 1}`.padStart(2, "0");
  const day = `${dateValue.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const startOfDay = (value) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const FeatureDetail = ({
  open,
  setShowModal,
  selectedFeature,
  updateData,
  selectedProduct,
  tenantID,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [formData, setFormData] = React.useState({
    SubscriptionEndDate: new Date(),
  });
  const [validateForm, setValidateForm] = React.useState(false);
  const requiredFields = ["SubscriptionEndDate"];
  const today = React.useMemo(() => startOfDay(new Date()), []);

  const handleInputChange = (event) => {
    const nextDate = event.target.value
      ? new Date(`${event.target.value}T00:00:00`)
      : null;

    setFormData((prevState) => ({
      ...prevState,
      SubscriptionEndDate: nextDate,
    }));
  };

  const isValidateDate = (shouldValidate, value) => {
    if (!shouldValidate || value == null) {
      return false;
    }

    return startOfDay(new Date(value)) < today;
  };

  const validateExpirationDate = () => {
    const expirationDate = formData?.SubscriptionEndDate;

    if (!expirationDate) {
      return false;
    }

    return startOfDay(new Date(expirationDate)) >= today;
  };

  React.useEffect(() => {
    if (selectedFeature) {
      const featurePermission = getSubscription(selectedFeature?.Permissions);
      if (featurePermission) {
        setFormData({
          ...featurePermission,
          SubscriptionEndDate: new Date(featurePermission?.SubscriptionEndDate),
        });
      } else {
        setFormData({
          TenantId: tenantID,
          FeatureId: selectedFeature?.feature_id,
          SubscriptionEndDate: new Date(),
        });
      }
    }
  }, [selectedFeature, tenantID]);

  React.useEffect(() => {
    if (selectedProduct) {
      setFormData({
        SubscriptionEndDate: new Date(),
      });
    }
  }, [selectedProduct]);

  const updateProduct = async () => {
    try {
      let lastResponse = null;

      if (!selectedProduct?.features || selectedProduct.features.length === 0) {
        return null;
      }

      for (const feature of selectedProduct.features) {
        let featureData;

        if (feature?.Permissions?.length > 0) {
          const tempData = getSubscription(feature?.Permissions);
          featureData = {
            TenantId: tempData?.TenantId,
            FeatureId: tempData?.FeatureId,
            SubscriptionEndDate:
              formData?.SubscriptionEndDate?.toLocaleString("en-US"),
          };
        } else {
          featureData = {
            TenantId: tenantID,
            FeatureId: feature?.feature_id,
            SubscriptionEndDate:
              formData?.SubscriptionEndDate?.toLocaleString("en-US"),
          };
        }

        lastResponse = await updateFeature({
          data: featureData,
        });
      }

      return lastResponse;
    } catch (error) {
      console.error("Error updating feature data:", error);
      throw error;
    }
  };

  const onSave = async () => {
    setValidateForm(true);

    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    if (!validateExpirationDate()) {
      return;
    }

    setLoading(true);

    const data = {
      TenantId: formData?.TenantId,
      FeatureId: formData?.FeatureId,
      SubscriptionEndDate:
        formData?.SubscriptionEndDate?.toLocaleString("en-US"),
    };

    try {
      setValidateForm(false);
      const response = selectedProduct
        ? await updateProduct()
        : await updateFeature({
            data,
            isServiceWorker: false,
          });

      if (response?.Message) {
        GenerateNotification(
          buildNotification({
            title: ERROR_MESSAGE?.title,
            description: response.Message,
            style: ERROR_MESSAGE?.style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        setShowModal(false);
        updateData();
        GenerateNotification(
          buildNotification(UPDATE_MESSAGE),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  const panelTitle = selectedProduct
    ? `Update ${selectedProduct?.display_name}`
    : `Update ${selectedFeature?.display_name ?? "Feature"}`;
  const panelDescription = selectedProduct
    ? "Apply a new expiration date across all features in this product."
    : "Adjust the subscription expiration date for this feature.";

  return (
    <ProductSheet
      open={open}
      onClose={() => setShowModal(false)}
      title={panelTitle}
      description={panelDescription}
      footer={
        <>
          <ProductButton
            variant="outline"
            onClick={() => setShowModal(false)}
            disabled={loading}
          >
            Cancel
          </ProductButton>
          <ProductButton onClick={onSave} disabled={loading}>
            {loading ? <SpinnerIcon /> : null}
            Submit
          </ProductButton>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-lg bg-background shadow-sm ring-1 ring-border/60">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                Expiration Date
              </p>
              <p className="text-xs text-muted-foreground">
                Choose today or a future date for this subscription.
              </p>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <input
              id="subscription-end-date"
              name="SubscriptionEndDate"
              type="date"
              className="flex h-11 w-full rounded-lg border border-border/70 bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-ring"
              value={toDateInputValue(formData?.SubscriptionEndDate)}
              onChange={handleInputChange}
              min={toDateInputValue(today)}
            />
            {isValidateDate(validateForm, formData?.SubscriptionEndDate) && (
              <p className="text-xs text-destructive">
                Expiration date must be today or a future date.
              </p>
            )}
          </div>
        </div>
      </div>
    </ProductSheet>
  );
};

export default FeatureDetail;
